export function parseMarkdownToHtml(markdown: string): string {
  if (!markdown) return "";
  
  // Escape HTML entities to protect against XSS (Mandatory web security check)
  let html = markdown
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Bold **text**, then italic *text* — bold first, or `**x**` would be read
  // as an italic wrapping a stray asterisk. The Privacy Policy's "Dutch
  // *Telecommunicatiewet*" rendered with its asterisks showing until this
  // existed. Single asterisks are only italics when they wrap a run that does
  // not start or end with whitespace, so a list marker or a stray `*` is left alone.
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/(^|[^*\w])\*(?!\s)([^*\n]+?)(?<!\s)\*(?!\w)/g, "$1<em>$2</em>");

  // A line that is only `---` is a horizontal rule, not the text "---".
  html = html.replace(/^\s*---\s*$/gm, "<hr class='my-8 border-divider'/>");
  
  // Inline code `code`
  html = html.replace(/`(.*?)`/g, "<code class='bg-blue-500/10 px-1.5 py-0.5 rounded font-mono text-brand-primary text-xs'>$1</code>");

  // Headings. A trailing `{#anchor}` becomes the element's id and is dropped
  // from the visible text — the Privacy Policy's cookie section is written
  // `### 7. Cookies and Similar Technologies {#cookies}` so that the cookie
  // notice can link to `/privacy#cookies`. Until this existed the marker
  // rendered literally on the page and the link scrolled nowhere.
  // `scroll-mt-24` keeps an anchored heading clear of the fixed marketing nav.
  const heading = (level: number, cls: string) => {
    const re = new RegExp(`^${"#".repeat(level)} (.*?)(?:\\s*\\{#([A-Za-z][\\w-]*)\\})?$`, "gm");
    html = html.replace(re, (_m, text: string, id?: string) =>
      `<h${level}${id ? ` id="${id}"` : ""} class='${cls} scroll-mt-24'>${text}</h${level}>`
    );
  };
  heading(3, "text-base font-bold text-primary mt-6 mb-2");
  heading(2, "text-lg font-bold text-primary border-b border-divider pb-1 mt-8 mb-3");
  heading(1, "text-2xl font-black text-primary tracking-tight mt-10 mb-4");

  // Unordered list items - ... or * ...
  html = html.replace(/^[-\*] (.*?)$/gm, "<li class='text-secondary' style=\"margin-bottom: 6px; display: list-item; list-style-type: disc;\">$1</li>");

  // Wrap consecutive <li> tags in a <ul> tag with inline styling
  html = html.replace(/(<li.*?>.*?<\/li>\n?)+/g, (match) => {
    return `<ul style="list-style-type: disc; padding-left: 20px; margin-top: 16px; margin-bottom: 16px; list-style-position: outside;">${match}</ul>\n`;
  });

  // Paragraphs (split by double newline, wrap non-empty blocks if not already tags)
  const paragraphs = html.split(/\n\n+/);
  html = paragraphs
    .map(p => {
      const trimmed = p.trim();
      if (!trimmed) return "";
      // If it's a list item, heading, or ul tag, don't wrap it in a <p> tag
      // `<h` also covers `<hr`.
      if (trimmed.startsWith("<h") || trimmed.startsWith("<li") || trimmed.startsWith("<ul")) {
        return trimmed;
      }
      return `<p class="mb-4 text-secondary leading-relaxed">${trimmed.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");

  return html;
}
